// As seções de detalhe de um atendimento.
//
// O drawer das Conversas e o dialog da tabela de Leads mostram exatamente a
// mesma coisa. Manter isso num lugar só evita que um dos dois fique para trás
// quando a engine muda.

import React from "react";
import { Box, Typography, Stack, Chip } from "@mui/material";

export function SectionTitle({ children }) {
    return (
        <Typography
            sx={{
                fontSize: 11,
                fontWeight: 800,
                color: "var(--cise-text-muted)",
                textTransform: "uppercase",
                letterSpacing: 0.8,
                mb: 0.5,
                mt: 2,
            }}
        >
            {children}
        </Typography>
    );
}

export function DetailRow({ label, value }) {
    if (value === null || value === undefined || value === "") return null;
    return (
        <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", py: 0.6 }}>
            <Typography sx={{ fontSize: 12, color: "var(--cise-text-muted)", minWidth: 120, flexShrink: 0 }}>
                {label}
            </Typography>
            <Typography sx={{ fontSize: 13, fontWeight: 700, textAlign: "right", ml: 1 }}>{value}</Typography>
        </Box>
    );
}

function Card({ children, tone }) {
    const toned = tone === "success";
    return (
        <Box
            sx={{
                bgcolor: toned ? "rgba(34,197,94,0.08)" : "var(--branco)",
                borderRadius: 1,
                border: toned ? "1px solid rgba(34,197,94,0.3)" : "1px solid var(--border)",
                px: 2,
                py: 1,
                mb: 1,
            }}
        >
            {children}
        </Box>
    );
}

// O que ainda falta coletar. É o que o operador mais precisa enxergar num
// atendimento em andamento.
function PendingFields({ missing }) {
    if (!missing?.length) return null;
    return (
        <Box sx={{ pt: 0.8, mt: 0.6, borderTop: "1px dashed var(--border)" }}>
            <Typography sx={{ fontSize: 11, color: "var(--cise-text-muted)", mb: 0.5 }}>Ainda falta</Typography>
            <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.5 }}>
                {missing.map((field) => (
                    <Chip
                        key={field}
                        label={field}
                        size="small"
                        color="warning"
                        variant="outlined"
                        sx={{ fontSize: 11, height: 20 }}
                    />
                ))}
            </Stack>
        </Box>
    );
}

export default function LeadProfileSections({ details, phone }) {
    if (!details) return null;

    const { guardian, learners = [], appointment, preference, contextChips = [] } = details;

    return (
        <>
            {guardian && (
                <>
                    <SectionTitle>Responsável</SectionTitle>
                    <Card>
                        <DetailRow label="Nome" value={guardian.name} />
                        <DetailRow label="Bairro" value={guardian.neighborhood} />
                        <DetailRow label="WhatsApp" value={phone} />
                        <PendingFields missing={guardian.missing} />
                    </Card>
                </>
            )}

            {learners.map((learner) => (
                <React.Fragment key={learner.id || learner.sectionLabel}>
                    <SectionTitle>{learner.sectionLabel}</SectionTitle>
                    <Card>
                        <DetailRow label="Nome completo" value={learner.name} />
                        <DetailRow label="Nascimento" value={learner.birthDate} />
                        <DetailRow
                            label="Idade"
                            value={
                                learner.age != null
                                    ? `${learner.age} anos`
                                    : learner.knownAgeOnly != null
                                        ? `${learner.knownAgeOnly} anos (sem data)`
                                        : null
                            }
                        />
                        <DetailRow label="Escola" value={learner.school} />
                        <DetailRow
                            label="Bairro"
                            value={
                                learner.neighborhood
                                    ? learner.neighborhoodInherited
                                        ? `${learner.neighborhood} (do responsável)`
                                        : learner.neighborhood
                                    : null
                            }
                        />
                        <PendingFields missing={learner.missing} />
                    </Card>
                </React.Fragment>
            ))}

            {appointment && (
                <>
                    <SectionTitle>Agendamento</SectionTitle>
                    <Card tone={appointment.status === "CONFIRMED" ? "success" : undefined}>
                        <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 0.5 }}>
                            <Typography sx={{ fontSize: 12, fontWeight: 800 }}>
                                {[appointment.weekday, appointment.dateFormatted].filter(Boolean).join(", ")}
                            </Typography>
                            <Chip
                                label={appointment.statusLabel}
                                size="small"
                                color={appointment.status === "CONFIRMED" ? "success" : "warning"}
                                sx={{ fontWeight: 700, height: 20, fontSize: 11 }}
                            />
                        </Stack>
                        <DetailRow label="Horário" value={appointment.time} />
                        <DetailRow label="Professor" value={appointment.teacherName} />
                        <DetailRow
                            label="Alunos"
                            value={appointment.students?.length ? appointment.students.join(", ") : null}
                        />
                    </Card>
                </>
            )}

            {preference?.summary && (
                <>
                    <SectionTitle>Preferência de Horário</SectionTitle>
                    <Card>
                        <DetailRow label="Pedido do lead" value={preference.summary} />
                    </Card>
                </>
            )}

            {contextChips.length > 0 && (
                <>
                    <SectionTitle>Contexto da Conversa</SectionTitle>
                    <Stack direction="row" flexWrap="wrap" sx={{ gap: 0.8, mb: 1 }}>
                        {contextChips.map((chip) => (
                            <Chip key={chip} label={chip} size="small" variant="outlined" sx={{ fontSize: 11, height: 22 }} />
                        ))}
                    </Stack>
                </>
            )}
        </>
    );
}
